import { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import IntentService, { errorMessage } from 'src/services/IntentService';
import { rubles, safeExternalUrl } from 'src/utils/intentEconomics';

import type { Lead, Processing, Report, Workspace } from 'src/services/IntentService';

export const stages: Record<string, string> = {
    new: 'Новый',
    working: 'В работе',
    contacted: 'Связались',
    qualified: 'Квалифицирован',
    won: 'Продажа',
    lost: 'Не купил',
    not_fit: 'Не подходит',
};
const sourceNames: Record<string, string> = {
    telegram: 'Telegram',
    vk: 'VK',
    max: 'MAX',
    youtube: 'YouTube',
    forum: 'Форум',
    web: 'Сайт',
};

export function LeadsPanel({
    workspace,
    mode = 'list',
    onLaunch,
    hideTitle = false,
}: {
    workspace: Workspace;
    mode?: 'list' | 'funnel';
    hideTitle?: boolean;
    onLaunch?: () => void;
}) {
    const [leads, setLeads] = useState<Lead[]>([]);
    const [total, setTotal] = useState(0);
    const [selected, setSelected] = useState<Lead | null>(null);
    const [filter, setFilter] = useState('all');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [retryKey, setRetryKey] = useState(0);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        let live = true;
        setLoading(true);
        setLeads([]);
        setTotal(0);
        setSelected(null);
        setError('');
        IntentService.leads(workspace.id)
            .then((data) => {
                if (live) {
                    setLeads(data.leads);
                    setTotal(data.total);
                }
            })
            .catch((e) => {
                if (live) setError(errorMessage(e));
            })
            .finally(() => {
                if (live) setLoading(false);
            });
        return () => {
            live = false;
        };
    }, [workspace.id, retryKey]);
    const save = async () => {
        if (!selected) return;
        setSaving(true);
        try {
            await IntentService.outcome(workspace.id, selected);
            setLeads((old) => old.map((l) => (l.id === selected.id ? selected : l)));
            setSelected(null);
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setSaving(false);
        }
    };
    const visible = leads.filter((l) => filter === 'all' || l.stage === filter);
    return (
        <Box>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
                gap={2}
            >
                <Typography
                    variant={hideTitle ? 'body2' : 'h5'}
                    component={hideTitle ? 'p' : 'h2'}
                    color={hideTitle ? 'text.secondary' : 'text.primary'}
                >
                    {hideTitle ? (
                        !loading && !error ? (
                            `Лидов: ${total}`
                        ) : (
                            ''
                        )
                    ) : (
                        <>
                            {mode === 'funnel' ? 'Воронка' : 'Лиды'}
                            {!loading && !error ? ` · ${total}` : ''}
                        </>
                    )}
                </Typography>
                <TextField
                    select
                    size="small"
                    label="Статус"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    sx={{ minWidth: 170 }}
                >
                    <MenuItem value="all">Все</MenuItem>
                    {Object.entries(stages).map(([id, label]) => (
                        <MenuItem
                            key={id}
                            value={id}
                        >
                            {label}
                        </MenuItem>
                    ))}
                </TextField>
            </Stack>
            {error && (
                <Alert
                    severity="error"
                    action={
                        <Button
                            size="small"
                            onClick={() => setRetryKey((value) => value + 1)}
                        >
                            Повторить
                        </Button>
                    }
                >
                    {error}
                </Alert>
            )}
            {loading ? (
                <Typography color="text.secondary">Загружаем лиды…</Typography>
            ) : error ? null : !visible.length ? (
                <Box className="intent-empty">
                    <Typography variant="h6">
                        {leads.length ? 'Нет лидов с этим статусом' : 'Лидов пока нет'}
                    </Typography>
                    <Typography color="text.secondary">
                        {leads.length
                            ? 'Выберите другой статус или покажите все лиды.'
                            : workspace.streams.some((s) => s.is_active)
                            ? 'Поиск работает. Новые запросы появятся здесь.'
                            : 'Выберите пакет и запустите поиск.'}
                    </Typography>
                    {leads.length ? (
                        <Button onClick={() => setFilter('all')}>Показать все лиды</Button>
                    ) : !workspace.streams.some((stream) => stream.is_active) && onLaunch ? (
                        <Button
                            sx={{ mt: 2 }}
                            variant="contained"
                            onClick={onLaunch}
                        >
                            Перейти к запуску
                        </Button>
                    ) : null}
                </Box>
            ) : mode === 'funnel' ? (
                <Box
                    className="project-funnel"
                    aria-label="Воронка потока"
                >
                    {Object.entries(stages).map(([stage, label]) => (
                        <section
                            key={stage}
                            className="project-funnel-column"
                            aria-label={label}
                        >
                            <h3>
                                {label} · {visible.filter((lead) => lead.stage === stage).length}
                            </h3>
                            {visible
                                .filter((lead) => lead.stage === stage)
                                .map((lead) => (
                                    <button
                                        key={lead.id}
                                        className="intent-lead-row"
                                        onClick={() => setSelected(lead)}
                                    >
                                        <strong>{lead.text}</strong>
                                        <span>{sourceNames[lead.source] || 'Открытый источник'}</span>
                                    </button>
                                ))}
                        </section>
                    ))}
                </Box>
            ) : (
                <Box className="intent-lead-list">
                    {visible.map((lead) => (
                        <button
                            key={lead.id}
                            className="intent-lead-row"
                            onClick={() => setSelected(lead)}
                        >
                            <span className="intent-lead-meta">
                                {sourceNames[lead.source] || 'Открытый источник'} ·{' '}
                                {new Date(lead.date).toLocaleDateString('ru-RU')}
                                <span>{stages[lead.stage]}</span>
                            </span>
                            <strong>{lead.text}</strong>
                            <span className="intent-lead-reason">{lead.reason || 'Совпадает с правилами потока'}</span>
                        </button>
                    ))}
                </Box>
            )}
            {total > leads.length && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    mt={2}
                >
                    Показаны последние {leads.length} из {total}. Полная история — в общей ленте.
                </Typography>
            )}
            <Dialog
                open={!!selected}
                onClose={() => !saving && setSelected(null)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Запрос клиента</DialogTitle>
                <DialogContent>
                    {selected && (
                        <Stack
                            spacing={2}
                            pt={1}
                        >
                            <Typography sx={{ whiteSpace: 'pre-wrap' }}>{selected.text}</Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                {selected.reason}
                            </Typography>
                            {safeExternalUrl(selected.url) && (
                                <Button
                                    component="a"
                                    href={safeExternalUrl(selected.url)}
                                    target="_blank"
                                    rel="noreferrer"
                                    variant="outlined"
                                >
                                    Открыть исходный запрос
                                </Button>
                            )}
                            <TextField
                                select
                                label="Результат — ваша отметка"
                                value={selected.stage}
                                onChange={(e) => setSelected({ ...selected, stage: e.target.value })}
                            >
                                {Object.entries(stages).map(([id, label]) => (
                                    <MenuItem
                                        key={id}
                                        value={id}
                                    >
                                        {label}
                                    </MenuItem>
                                ))}
                            </TextField>
                            {selected.stage === 'won' && (
                                <TextField
                                    label="Выручка, ₽"
                                    type="number"
                                    value={selected.revenue ?? ''}
                                    inputProps={{ min: 0, step: 0.01 }}
                                    onChange={(e) => setSelected({ ...selected, revenue: e.target.value || null })}
                                    helperText="Оставьте пустым, если сумма пока неизвестна"
                                />
                            )}
                            <TextField
                                label={selected.stage === 'not_fit' ? 'Причина несоответствия' : 'Заметка'}
                                multiline
                                value={selected.note}
                                inputProps={{ maxLength: 500 }}
                                onChange={(e) => setSelected({ ...selected, note: e.target.value })}
                            />
                            {selected.stage === 'not_fit' && (
                                <Typography variant="body2">
                                    Отметка сохраняет причину. Компенсация не начисляется автоматически: проверка
                                    доступна через поддержку.
                                </Typography>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setSelected(null)}
                        disabled={saving}
                    >
                        Закрыть
                    </Button>
                    <Button
                        variant="contained"
                        onClick={save}
                        disabled={saving}
                    >
                        {saving ? 'Сохраняем…' : 'Сохранить'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export function ResultsPanel({ workspace }: { workspace: Workspace }) {
    const [report, setReport] = useState<Report | null>(null);
    const [error, setError] = useState('');
    useEffect(() => {
        let live = true;
        IntentService.results(workspace.id)
            .then((r) => {
                if (live) setReport(r);
            })
            .catch((e) => {
                if (live) setError(errorMessage(e));
            });
        return () => {
            live = false;
        };
    }, [workspace.id]);
    if (error) return <Alert severity="error">{error}</Alert>;
    if (!report) return <Typography>Загружаем результаты…</Typography>;
    return (
        <Stack spacing={3}>
            <div>
                <Typography
                    variant="h5"
                    component="h2"
                >
                    Результаты
                </Typography>
                <Typography color="text.secondary">{report.period}</Typography>
            </div>
            <Box className="intent-result-grid">
                <div>
                    <span>Доставлено лидов</span>
                    <strong>{report.delivered}</strong>
                </div>
                <div>
                    <span>Подтверждённые продажи</span>
                    <strong>{report.results_known ? report.won : 'Нет данных'}</strong>
                </div>
                <div>
                    <span>Выручка по отметкам</span>
                    <strong>{rubles(report.revenue)}</strong>
                </div>
            </Box>
            <Typography color="text.secondary">
                Результат известен по {report.results_known} из {report.delivered} лидов. Незавершённые и незаполненные
                результаты не считаются отказами.
            </Typography>
            {!report.revenue_complete && (
                <Alert severity="info">Данные о выручке неполные. Это не итоговый финансовый результат потока.</Alert>
            )}
            <Box className="intent-order-summary">
                <dl>
                    <div>
                        <dt>Куплено пакетов лидов на</dt>
                        <dd>{rubles(report.lead_purchases)}</dd>
                    </div>
                    <div>
                        <dt>Стоимость использованных лидов</dt>
                        <dd>{rubles(report.consumed_lead_cost)}</dd>
                    </div>
                    <div>
                        <dt>Стоимость ИИ-обработки</dt>
                        <dd>{rubles(report.processing_cost)}</dd>
                    </div>
                </dl>
                <Typography
                    color="text.secondary"
                    variant="body2"
                >
                    {report.processing_cost_note}
                </Typography>
            </Box>
            {!report.cost_basis_complete && (
                <Alert severity="warning">
                    История начислений содержит корректировки без точной стоимости. Полный расход не вычислен.
                </Alert>
            )}
            <Typography variant="h6">Обработка</Typography>
            <Box className="intent-stage-list">
                {Object.entries(report.stages).map(([stage, count]) => (
                    <div key={stage}>
                        <span>{stages[stage]}</span>
                        <strong>{count}</strong>
                    </div>
                ))}
            </Box>
        </Stack>
    );
}

export function ProcessingPanel({ workspace, dialogs }: { workspace: Workspace; dialogs: number | null }) {
    const [data, setData] = useState<Processing | null>(null);
    const [error, setError] = useState('');
    useEffect(() => {
        let live = true;
        IntentService.processing(workspace.id)
            .then((r) => {
                if (live) setData(r);
            })
            .catch((e) => {
                if (live) setError(errorMessage(e));
            });
        return () => {
            live = false;
        };
    }, [workspace.id]);
    const stream = workspace.streams[0];
    return (
        <Stack spacing={3}>
            <Typography
                variant="h5"
                component="h2"
            >
                Обработка лидов
            </Typography>
            <Typography color="text.secondary">
                Доступно на аккаунте: {dialogs === null ? 'уточняется' : dialogs} ИИ-диалогов. Этот остаток общий для
                потоков.
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
            <Box className="intent-two-columns">
                <Box className="intent-plain-block">
                    <Typography variant="h6">Квалификация</Typography>
                    <Typography color="text.secondary">
                        Потребность, регион, сроки. Передача подходящего клиента человеку.
                    </Typography>
                </Box>
                <Box className="intent-plain-block">
                    <Typography variant="h6">Полезная рекомендация</Typography>
                    <Typography color="text.secondary">
                        Страница, материал или канал из вашей базы знаний. Переход и подписка — отдельные результаты.
                    </Typography>
                </Box>
            </Box>
            {!data?.agents.length && (
                <Alert severity="info">ИИ-менеджер не подключён. Можно получать лиды и отвечать самостоятельно.</Alert>
            )}
            <Button
                variant="contained"
                component="a"
                href={'/ai-managers/new' + (stream ? '?streamId=' + stream.id : '')}
                sx={{ alignSelf: 'flex-start' }}
            >
                Настроить ИИ-менеджера
            </Button>
            {data?.agents.map((a) => (
                <Box
                    key={a.id}
                    className="intent-plain-block"
                >
                    <Typography variant="h6">{a.name}</Typography>
                    <Typography color="text.secondary">
                        {a.status === 'active' ? 'Включён' : 'Проверьте готовность подключения'}
                    </Typography>
                    <Button
                        component="a"
                        href={'/ai-managers/' + a.id}
                    >
                        Открыть менеджера
                    </Button>
                </Box>
            ))}
            <Typography
                variant="body2"
                color="text.secondary"
            >
                Сначала проверьте знания и тестовый диалог, затем подключите канал. Найденный лид не означает
                отправленное сообщение. Исходящие действия — только в согласованном сценарии.
            </Typography>
            {!!data?.conversations.length && (
                <Typography variant="body2">
                    В потоке отображается {data.conversations.length} последних диалогов. Переписка и подтверждения
                    отправки — в карточке менеджера.
                </Typography>
            )}
        </Stack>
    );
}
